import { SavedDataViewResource } from '../Api/SavedDataView'
import {goTo} from "../../utils";

export let DataViewHelpers = {
    offset(state) {
        return (state.currentPage - 1) * state.perPage;
    },
    previousOffset(state) {
        if (state.resultSet) {
            return (parseInt(this.offset(state)) - parseInt(state.resultSet.limit ?? state.resultSet.per_page));
        }
    },
    nextOffset(state) {
        if (state.resultSet) {
            return (parseInt(this.offset(state)) + parseInt(state.resultSet.limit ?? state.resultSet.per_page));
        }
    },
    hasMorePages(state) {
        return state.resultSet && (this.nextOffset(state) < state.resultSet.total);
    },
    hasPreviousPages(state) {
        return state.resultSet && (this.previousOffset(state) >= 0);
    },
    lastResult(state) {
        if (!state.resultSet) {
            return true;
        }

        return (this.hasMorePages(state)) ? state.resultSet.to : state.resultSet.total;
    },
    currentFiltersWithOverrides(state) {
        let overrides = {}
        let currentFilters = {}

        for (let i in state.currentFilters) {
            currentFilters[i] = state.currentFilters[i]
        }

        for (let i in state.filterOverrides) {
            if (state.filterOverrides[i].value) {
                overrides[i] = state.filterOverrides[i].value
            }
        }

        return _.merge(currentFilters, overrides);
    },
    generateGetRequestParams(context) {
        let params = Object.assign({}, context.state.additionalDefaultParams, { 
            with: context.state.relations, 
            sort_order: context.state.currentSortOrder,
            offset: context.getters.offset,
            limit: context.state.perPage,
            filters: JSON.stringify(DataViewHelpers.currentFiltersWithOverrides(context.state)),
            page: context.state.currentPage,
            per_page: context.state.perPage,
        });

        if (context.state.searchTerm && context.state.searchTerm.length > 0) {
            params.query = context.state.searchTerm
        } else {
            delete(params.query)
        }

        return params
    },
    generateExportUri(state, format, columns, title, groupBy = null, subGroupBy = null) {
        let queryString = '?export=' + encodeURIComponent(format);
            queryString += '&title=' + encodeURIComponent(title && title.length > 0 ? title : state.title);
            queryString += '&filters=' + encodeURIComponent(JSON.stringify(DataViewHelpers.currentFiltersWithOverrides(state)));
            queryString += '&sort_order=' + encodeURIComponent(state.currentSortOrder);

            if (groupBy) {
                queryString += '&group_by=' + encodeURIComponent(groupBy)

                if (subGroupBy) {
                    queryString += '&sub_group_by=' + encodeURIComponent(subGroupBy)
                }
            }

        for (let i in columns) {
            queryString += '&column[]=' + encodeURIComponent(columns[i])
        }

        return [
            state.resource._buildSegments([]),
            queryString
        ].join('');
    }
}

const defaultStateGenerator = () => {
    return {
        title: 'All',
        searchTerm: null,
        availableFilters: null,
        availableSortOrders: null,
        isCustomView: false,
        isSavedView: false,
        systemDefaultView: null,
        savedViewId: null,
        savedViews: [],
        defaultFilters: {},
        currentFilters: {},
        filterOverrides: {},
        currentSortOrder: null,
        defaultSortOrder: null,
        perPage: 25,
        currentPage: 1,
        resultSet: null,
        resource: null,
        relations: [],
        additionalDefaultParams: {},
        hasResourceOptions: true,
        isBusy: true,
        namespacesToLoad: [],
        currentNamespace: null,
        layout: false,
        defaultLayout: false,
    }
}

let getDelay;

export default {
    namespaced: true,
    state: defaultStateGenerator(),
    mutations: {
        setDefaultState(state) {
            Object.assign(state, defaultStateGenerator())
        },
        setSearchTerm(state, term) {
            state.searchTerm = term
        },
        setCurrentNamespace(state, namespace) {
            state.currentNamespace = namespace
        },
        setNamespacesToLoad(state, namespaces) {
            state.namespacesToLoad = namespaces
        },
        setHasResourceOptions(state, hasOptions) {
            state.hasResourceOptions = hasOptions;
        },
        setAdditionalDefaultParams(state, params) {
            state.additionalDefaultParams = params;
        },
        setLayout(state, layout) {
            state.layout = layout;
        },
        setDefaultLayout(state, layout) {
            state.defaultLayout = layout;
        },
        setIsPresetView(state) {
            state.isCustomView = false;
            state.isSavedView = false;
            state.savedViewId = null;
        },
        setIsCustomView(state) {
            state.isCustomView = true;
            state.isSavedView = false;
            state.savedViewId = null;
            state.title = 'Custom View';
        },
        setIsSavedView(state, id) {
            if (id !== null && id !== false) {
                state.savedViewId = id;
                state.isSavedView = true;
                state.isCustomView = false;
            } else {
                state.savedViewId = null;
                state.isSavedView = false;
                state.isCustomView = false;
            }
        },
        setSystemDefaultView(state, { name, filters, sort_order }) {
            state.systemDefaultView = {
                name,
                filters,
                sort_order
            }
        },
        setPage(state, page) {
            state.currentPage = page;
        },
        setDefaultFilters(state, filters) {
            Vue.set(state, 'defaultFilters', filters)

            if (!state.resultSet) {
                Vue.set(state, 'currentFilters', _.cloneDeep(filters));
            }
        },
        setDefaultFilter(state, filter) {
            filter = _.cloneDeep(filter)

            Vue.set(state.defaultFilters, filter.key, filter.value);

            if (!state.resultSet) {
                Vue.set(state.currentFilters, filter.key, filter.value);
            }
        },
        removeDefaultFilter(state, key) {
            delete(state.defaultFilters[key])

            if (!state.resultSet) {
                delete(state.currentFilters[key])
            }
        },
        setFilter(state, filter) {
            Vue.set(state.currentFilters, filter.key, filter.value);
        },
        removeFilter(state, filterKey) {
            Vue.delete(state.currentFilters, filterKey);
        },
        setFilters(state, filters) {
            Vue.set(state, 'currentFilters', _.cloneDeep(filters))
        },
        setFilterOverride(state, filter) {
            Vue.set(state.filterOverrides, filter.key, filter);
        },
        setFilterOverrides(state, filters) {
            Vue.set(state, 'filterOverrides', filters)
        },
        setTitle(state, title) {
            state.title = title;
        },
        setDefaultSortOrder(state, order) {
            state.defaultSortOrder = order;

            if (!state.currentSortOrder) {
                state.currentSortOrder = order;
            }
        },
        setSortOrder(state, order) {
            state.currentSortOrder = order;
        },
        setAvailableFilter(state, {key, filter}) {
            state.availableFilters[key] = filter;
        },
        setAvailableFilters(state, filters) {
            state.availableFilters = filters;
        },
        setAvailableSortOrders(state, orders) {
            state.availableSortOrders = orders;
        },
        setResourceRelations(state, relations) {
            state.relations = relations;
        },
        setResultsSet(state, resultSet) {
            state.resultSet = resultSet;
        },
        setPerPage(state, perPage) {
            state.perPage = perPage;
        }
    },
    getters: {
        getAvailableFilter: (state) => (key) => {
            if (state.availableFilters) {
                return state.availableFilters.find(item => item.key === key)
            }
        },
        offset(state) {
            return DataViewHelpers.offset(state);
        },
        previousOffset(state) {
            return DataViewHelpers.previousOffset(state);
        },
        nextOffset(state) {
            return DataViewHelpers.nextOffset(state);
        },
        hasMorePages(state) {
            return DataViewHelpers.hasMorePages(state);
        },
        hasPreviousPages(state) {
            return DataViewHelpers.hasPreviousPages(state);
        },
        lastResult(state) {
            return DataViewHelpers.lastResult(state);
        },
        hasResults(state) {
            return (state.resultSet && state.resultSet.results && state.resultSet.total > 0) ? true : false;
        },
        isFiltered(state) {
            return _.toArray(state.currentFilters).length > 0
        },
        currentFiltersWithOverrides(state) {
            return DataViewHelpers.currentFiltersWithOverrides(state)
        },
        queryString(state) {
            let query = '?';

            // Add the current filters
            for (let i in state.currentFilters) {
                query += (encodeURIComponent(i) + '=' + encodeURIComponent(JSON.stringify(state.currentFilters[i])) + '&');
            }

            // and the sort order...
            if (state.currentSortOrder && state.currentSortOrder !== state.defaultSortOrder) {
               query += 'order=' + encodeURIComponent(state.currentSortOrder) + '&';
            }

            // the page.
            query += 'page=' + encodeURIComponent(state.currentPage);

            // results per page
            query += '&per_page=' + encodeURIComponent(state.perPage);

            // title
            query += '&title=' + encodeURIComponent(state.title);

            // layout
            if (state.layout) {
                query += '&layout=' + encodeURIComponent(state.layout)
            }

            if (state.isSavedView) {
                query += '&savedViewId=' + state.savedViewId
            }

            if (state.isCustomView) {
                query += '&custom=1'
            }

            if (state.searchTerm && state.searchTerm.length > 0) {
                query += '&searchTerm=' + encodeURIComponent(state.searchTerm)
            }

            return query;
        },
        exportLink(state) {
            return (format, columns = ['*'], title = null, groupBy = null, subGroupBy = null) => {
                return DataViewHelpers.generateExportUri(state, format, columns, title, groupBy, subGroupBy)
            }
        },
        currentSavedView(state) {
            return state.savedViews.find(view => {
                return view.id === state.savedViewId
            })
        },
        defaultSavedView(state) {
            return state.savedViews.find(view => {
                return view.is_default
            })
        },
        isSystemDefaultView(state) {
            return (
                JSON.stringify(state.systemDefaultView?.filters) === JSON.stringify(state.currentFilters) &&
                state.systemDefaultView?.sort_order === state.currentSortOrder
            )
        },
        isCustomDefaultView(state) {
            const defaultViewId = state.savedViews.find(view => {
                return view.is_default
            })

            return defaultViewId && defaultViewId.id === state.savedViewId
        },
        savedViewMenuOptions(state) {
            // Map the users saved views.
            let savedViews = state.savedViews.filter(item => !item.is_default)
                .map(item => {
                    return {
                        label: item.name,
                        filters: JSON.parse(item.filters),
                        sort: item.sort_order,
                        saved_data_view_id: item.id
                    }
                });

            if (savedViews.length > 0) {
                savedViews.unshift({ type: 'subtitle', label: 'Saved Views' })
                savedViews.unshift({ type: 'divider' });
            }

            return savedViews;
        }
    },
    actions: {
        search(context, term) {
            return new Promise((resolve, reject) => {
                context.commit('setSearchTerm', term)
                context.commit('setPage', 1)
                context.dispatch('setQueryString')

                context.dispatch('get').then(
                    resolve, (e) => handleApiError(e, reject)
                )
            })
        },
        resolveFiltersAndOrderFromUrl(context) {
            // Resolve the title.
             let title = getQueryParameterByName('title');

             if (title) {
                context.commit('setTitle', title);
             }

            // Resolve the search term.
            let searchTerm = getQueryParameterByName('searchTerm');

            if (searchTerm) {
                context.commit('setSearchTerm', searchTerm)
            }

            // Set the requested filters.
            for (let i in context.state.availableFilters) {
                let value = getQueryParameterByName(context.state.availableFilters[i].key);

                if (value) {
                    let parsedValue;

                    try {
                        parsedValue = JSON.parse(value)
                    } catch (e) {
                        parsedValue = value
                    }

                    context.commit('setFilter', {
                        key: context.state.availableFilters[i].key, 
                        value: parsedValue
                    });
                }
            }

            // Set current page from query.
            let page = getQueryParameterByName('page');

            if (page) {
                context.commit('setPage', parseInt(page));
            }

            // Set per page from query.
            let perPage = getQueryParameterByName('per_page');

            if (perPage) {
                context.commit('setPerPage', perPage)
            }

            // Set the current sort order.
            let sortOrder = getQueryParameterByName('order');

            if (sortOrder) {
                context.commit('setSortOrder', sortOrder)
            }

            // Set the layout.
            if (getQueryParameterByName('layout')) {
                context.commit('setLayout', getQueryParameterByName('layout'));
            }

            // Set the custom & saved view markers.
            if (getQueryParameterByName('savedViewId')) {
                context.commit('setIsSavedView', getQueryParameterByName('savedViewId'));
            }

            if (getQueryParameterByName('custom') === '1') {
                context.commit('setIsCustomView');
            }
        },
        setResource(context, { resource, resolveParamsFromUrl = true }) {
            return new Promise((resolve, reject) => {
                context.state.resource = resource;

                if (!context.state.currentNamespace) {
                    context.state.currentNamespace = resource.getHandle()
                }
                
                if (0 === context.state.namespacesToLoad.length) {
                    context.state.namespacesToLoad = [context.state.currentNamespace]
                }

                if (context.state.hasResourceOptions) {
                    context.state.resource.options().then((response) => {

                        context.commit('setAvailableFilters', response.data.filters);

                        context.commit('setAvailableSortOrders', response.data.orders);

                        // Populate the filter overrides with the definitions provided from 
                        // the backend or remove it altogether.
                        let overrides = context.state.filterOverrides;

                        for (let i in overrides) {
                            let filter = response.data.filters.find(item => item.key === overrides[i].key)
                            if (filter) {
                                overrides[i] = _.merge(filter, overrides[i])
                            } else {
                                delete(overrides[i]);
                            }
                        }

                        context.commit('setFilterOverrides', overrides);

                        // Setup the sort orders with reactivity.
                        for (let i in response.data.orders) {
                            if (response.data.orders[i].default && !context.state.currentSortOrder) {
                                if (!context.state.defaultSortOrder) {
                                    context.commit('setDefaultSortOrder', response.data.orders[i].value);
                                }

                                context.commit('setSortOrder', response.data.orders[i].value);
                            }
                        }

                        if (resolveParamsFromUrl) {
                            context.dispatch('resolveFiltersAndOrderFromUrl');
                        }

                        resolve();
                    }, (e) => handleApiError(e, reject));
                } else {

                    if (resolveParamsFromUrl) {
                        context.dispatch('resolveFiltersAndOrderFromUrl');
                    }
                    resolve();
                }
            });
        },
        setResourceAndLoad(context, resource) {
            return new Promise((resolve, reject) => {
                context.dispatch('setResource', { resource, resolveParamsFromUrl: false }).then(() => {
                    return context.dispatch('load')
                }).then(() => {
                    // Resource (vehicles,deals,leads,etc) list views pass a URL parameter to the page to indicate that
                    // this is the default view for the resource. We look for this parameter and if it exists we then
                    // look for a custom default view for the user.
                    const isContextDefaultView = context.getters.isSystemDefaultView

                    // Check whether we're currently set to a saved view instance that is not the users custom default view.
                    const isNotCurrentSavedView = (!context.getters.currentSavedView ||
                        context.getters.currentSavedView && context.getters.defaultSavedView.id !== context.getters.currentSavedView.id
                    )

                    if (isContextDefaultView && context.getters.defaultSavedView && isNotCurrentSavedView) {
                        context.dispatch('setViewFromSavedView', { savedViewId: context.getters.defaultSavedView.id, load: false })
                    } else {
                        context.dispatch('resolveFiltersAndOrderFromUrl')
                    }

                    return true
                }).then(() => {
                    return context.dispatch('get')
                }).then(() => {
                    resolve();
                }, (e) => handleApiError(e, reject));
            })
        },
        setViewFromOptionAndLoad(context, option) {
            return context.dispatch('setViewFromOption', { option, load: true })
        },
        setViewFromOption(context, { option, load = true }) {
            return new Promise((resolve, reject) => {
                if (option.filters) {
                    let savedViewId = (
                        option.saved_data_view_id ?
                            option.saved_data_view_id :
                            null
                    );

                    if (savedViewId) {
                        return context.dispatch('setViewFromSavedView', { savedViewId, load })
                    } else {
                        return context.dispatch('setView', {
                            filters: option.filters,
                            sort_order: option.sort,
                            name: option.label,
                            load
                        })
                    }
                }
            })
        },
        setViewFromSavedView(context, { savedViewId, load = true }) {
            const savedView = context.state.savedViews.find(view => {
                return view.id === savedViewId
            })

            return context.dispatch('setView', {
                name: savedView.name,
                filters: JSON.parse(savedView.filters),
                sort_order: savedView.sort_order,
                saved_view_id: savedViewId,
                load
            })
        },
        setView(context, { filters, name,  saved_view_id = null, sort_order = null, load = true }) {
            return new Promise((resolve, reject) => {

                context.commit('setIsSavedView', saved_view_id)

                context.commit('setFilters', filters)

                if (sort_order) {
                    context.commit('setSortOrder', sort_order)
                }

                context.commit('setPage', 1)

                if (load) {
                    return context.dispatch('get').then(() => {
                        context.commit('setTitle', name)
                        context.dispatch('setQueryString')
                        resolve();
                    }, reject);
                } else {
                    context.commit('setTitle', name)
                    context.dispatch('setQueryString')
                }

                return true;
            })
        },
        softReset(context) {
            context.commit('setLayout', context.state.defaultLayout)
            context.commit('setSearchTerm', null)
            context.commit('setFilters', context.state.defaultFilters);
            context.commit('setSortOrder', context.state.defaultSortOrder);
            context.commit('setPerPage', 25);
            context.commit('setPage', 1);
            context.commit('setResultsSet', null);
            context.commit('setTitle', 'All');
            context.commit('setIsSavedView', false);
        },
        hardReset(context) {
            context.commit('setDefaultState')
        },
        reset(context) {
            context.dispatch('softReset')

            context.dispatch('setQueryString')

            return context.dispatch('get');
        },
        exportTo(context, { format, columns = ['*'], title = null, groupBy = null }) {
            return new Promise((resolve, reject) => {

                let uri = DataViewHelpers.generateExportUri(context.state, format, columns, title, groupBy)

                let w = window.open(uri);

                w.addEventListener("close", function( event ) {
                    clearTimeout(t);
                    resolve();
                }, false);
            });
        },
        load(context) {
            return new Promise((resolve, reject) => {

                let params = { 
                    namespaces: context.state.namespacesToLoad
                };    

                SavedDataViewResource.all(params).then(response => {
                    context.state.savedViews = response.data;
                    resolve(response);
                }, (e) => handleApiError(e, reject))
            })
        },
        makeDefault(context) {
            // Is already a private saved view?
            if (context.getters.defaultSavedView) {
                // Yes - Update the view to be the users default
                return context.dispatch('save', {
                    name: context.state.title,
                    is_private: true,
                    is_default: true,
                    id: context.getters.defaultSavedView.id,
                })
            } else {
                // No - Create a new private view and make it the users default
                return context.dispatch('save', {
                    name: context.state.title,
                    is_private: true,
                    is_default: true,
                })
            }
        },
        unsetDefault(context) {
            context.state.isBusy = true;

            return SavedDataViewResource.destroy(context.getters.defaultSavedView.id).then(() => {
                goTo(window.location.pathname)
            })
        },
        save(context, { id, name, is_private, is_default }) {
            context.state.isBusy = true;

            return new Promise((resolve, reject) => {
                let data = { 
                    name,
                    is_private,
                    is_default,
                    sort_order: context.state.currentSortOrder,
                    filters: JSON.stringify(DataViewHelpers.currentFiltersWithOverrides(context.state)),
                    namespace: context.state.currentNamespace
                };

                SavedDataViewResource.storeOrUpdate(id, data).then(response => {
                    context.commit('setTitle', name);
                    context.commit('setIsSavedView', response.data.id);
                    context.dispatch('setQueryString');
                    context.dispatch('load').then(() => {
                        context.state.isBusy = false;
                        resolve(response);
                    }, (e) => handleApiError(e, reject));
                }, (e) => handleApiError(e, reject))
            });
        },
        delete(context, id) {
            context.state.isBusy = true;

            return new Promise((resolve, reject) => {
                SavedDataViewResource.destroy(id).then(response => {
                    context.dispatch('load').then(() => {
                        context.dispatch('reset').then(resolve, (e) => handleApiError(e, reject));
                    }, (e) => handleApiError(e, reject))
                }, (e) => handleApiError(e, reject))
            });
        },
        get(context) {
            context.state.isBusy = true;

            return new Promise((resolve, reject) => {
                let params = DataViewHelpers.generateGetRequestParams(context);
                
                context.state.resource.all(params).then((response) => {
                    let data = response.data

                    // New resource based API responses will have links present,
                    // they need to be reformatted to maintain compatibility.
                    if (data.links) {
                        data = {
                            ...response.data.meta,
                            results: response.data.data,
                            links: response.data.links
                        }
                    }

                    context.commit('setResultsSet', data);

                    resolve(response);
                }, (e) => handleApiError(e, reject)).finally(() => {
                    context.state.isBusy = false;
                })
            });
        },
        getAll(context, callback = null) {
            let onProgress
            let onData
            if (callback && callback.hasOwnProperty('onData') && callback.hasOwnProperty('onProgress')) {
                onData = callback.onData
                onProgress = callback.onProgress
            } else if(typeof callback === 'object') {
                onProgress = callback
            }

            context.state.isBusy = true;

            return new Promise((resolve, reject) => {
                // Make the first request to get the context for subsequent requests.
                context.dispatch('get').then(response => {
                    context.state.isBusy = true;

                    let maxConcurrentRequests = 4;
                    let queue = [];
                    let currentSegmentId = 0;
                    let lastReportedTotal = 0;

                    // Create the queue.
                    while (context.getters.hasMorePages) {
                        context.commit('setPage', context.state.currentPage + 1)
                        queue.push(DataViewHelpers.generateGetRequestParams(context))
                    }

                    context.commit('setPage', 1)

                    // Report Segment Complete
                    const reportSegmentComplete = (response) => {
                        if (onProgress && response.data.to > lastReportedTotal) {
                            let current = lastReportedTotal = Math.min(response.data.to, response.data.total)

                            onProgress(
                                current,
                                response.data.total,
                                Math.round(current / (response.data.total / 100)),
                                response.data
                            )
                        }

                        if (onData) {
                            onData(response.data)
                        }
                    }

                    reportSegmentComplete(response)

                    // Fetch segment.
                    let fetchQueueSegment = (segmentId) => {
                        return context.state.resource.all(queue[segmentId]).then((response) => {
                            queue[segmentId] = response.data

                            reportSegmentComplete(response)
                        }, (e) => handleApiError(e, reject));  
                    }

                    // Execute the queue.
                    let executeQueueSegment = () => {
                        if (currentSegmentId >= queue.length) {
                            let results = context.state.resultSet
                            results.from = 1
                            results.offset = 0

                            for (let i in queue) {
                                results.results = _.concat(results.results, queue[i].results)
                                results.to = queue[i].to
                            }

                            context.commit('setResultsSet', results);

                            resolve(results);

                            return;
                        }

                        let segments = [];

                        for (let i = 0; i < maxConcurrentRequests && currentSegmentId < queue.length; i++) {
                            segments.push(fetchQueueSegment(currentSegmentId))
                            currentSegmentId++;
                        }

                        Promise.all(segments).then(() => {
                            executeQueueSegment();
                        })
                    }

                    executeQueueSegment();
                })
            });
        },
        next(context) {
            if (context.getters.hasMorePages) {
                context.commit('setPage', context.state.currentPage + 1);
                context.dispatch('get');
            }
        },
        previous(context) {
            if (context.getters.hasPreviousPages) { 
                context.commit('setPage', context.state.currentPage - 1);
                context.dispatch('get');
            }
        },
        setQueryString(context) {
            let path = window.location.pathname;

            history.pushState(null, null, context.getters.queryString);
        },
    }
}